import React, { useMemo } from 'react'

/**
 * Units/transformations are never computed client-side or server-side: they
 * are whatever combinations already exist as real columns (Unit,
 * Effective_Unit, Transformation) in the rows CKAN returns, discovered via
 * IndicatorCountryMetadata and cached by useIndicatorMetadataCache.
 * Everything available comes pre-checked; the user may uncheck entries.
 */
function DataCenterUnitsPanel({
  selectedIndicatorNames,
  metadataByIndicator,
  frequency,
  loading,
  uncheckedUnits,
  uncheckedTransformations,
  onToggleUnit,
  onToggleTransformation,
}) {
  const availableUnits = useMemo(() => {
    const set = new Set()
    selectedIndicatorNames.forEach((name) => {
      const freqData = metadataByIndicator[name]?.[frequency]
      if (!freqData) return
      Object.keys(freqData).forEach((unit) => set.add(unit))
    })
    return Array.from(set).sort()
  }, [selectedIndicatorNames, metadataByIndicator, frequency])

  const availableTransformations = useMemo(() => {
    const set = new Set()
    selectedIndicatorNames.forEach((name) => {
      const freqData = metadataByIndicator[name]?.[frequency]
      if (!freqData) return
      Object.values(freqData).forEach((transformations) => {
        transformations.forEach((t) => set.add(t))
      })
    })
    return Array.from(set).sort()
  }, [selectedIndicatorNames, metadataByIndicator, frequency])

  // Transformations are few in the real system, so instead of only showing
  // whatever the current selection happens to have, we show every
  // transformation ever seen across the whole session's metadata cache —
  // the ones that don't apply to the current selection are rendered
  // disabled rather than hidden, same pattern as the dashboard indicator
  // widget. This keeps the chip set stable as the user swaps indicators
  // instead of the panel reflowing every time.
  const allKnownTransformations = useMemo(() => {
    const set = new Set(availableTransformations)
    Object.values(metadataByIndicator || {}).forEach((freqMap) => {
      const freqData = freqMap?.[frequency]
      if (!freqData) return
      Object.values(freqData).forEach((transformations) => {
        transformations.forEach((t) => set.add(t))
      })
    })
    return Array.from(set).sort()
  }, [metadataByIndicator, frequency, availableTransformations])

  const hasSelection = selectedIndicatorNames.length > 0

  return (
    <div className="dc-panel" id="dcPanelUnits">
      <div className="dc-panel-head">
        <div className="dc-panel-head-left">
          <span className="dc-badge-step">3</span>
          <div>
            <h2 className="dc-panel-title">Units &amp; transformations</h2>
            <span className="dc-panel-sub">Only combinations that exist for your selection are shown</span>
          </div>
        </div>
      </div>
      <div className="dc-panel-body">
        {!hasSelection ? (
          <div className="dc-empty">Select at least one country and indicator to see available units.</div>
        ) : loading ? (
          <div className="dc-empty">Loading available units and transformations…</div>
        ) : (
          <>
            <div className="dc-field">
              <div className="dc-label">
                <span>Units</span>
                <span className="dc-label-hint">{availableUnits.length === 0 ? 'None found' : 'Uncheck to exclude'}</span>
              </div>
              <div className="dc-chips">
                {availableUnits.length === 0 && <span className="dc-inline-note">No unit metadata found for this frequency yet.</span>}
                {availableUnits.map((unit) => (
                  <button
                    type="button"
                    key={unit}
                    className={`dc-chip${!uncheckedUnits.has(unit) ? ' is-on' : ''}`}
                    onClick={() => onToggleUnit(unit)}
                  >
                    {unit}
                  </button>
                ))}
              </div>
            </div>
            <div className="dc-field">
              <div className="dc-label">
                <span>Transformations</span>
                <span className="dc-label-hint">{allKnownTransformations.length === 0 ? 'None found' : 'Uncheck to exclude — greyed out ones don’t apply to this selection'}</span>
              </div>
              <div className="dc-chips">
                {allKnownTransformations.length === 0 && <span className="dc-inline-note">No transformation metadata found for this frequency yet.</span>}
                {allKnownTransformations.map((transformation) => {
                  const isAvailable = availableTransformations.includes(transformation)
                  return (
                    <button
                      type="button"
                      key={transformation}
                      disabled={!isAvailable}
                      className={`dc-chip${isAvailable && !uncheckedTransformations.has(transformation) ? ' is-on' : ''}`}
                      onClick={() => onToggleTransformation(transformation)}
                    >
                      {transformation}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className="dc-note">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-5M12 8h.01" /></svg>
              <div>Units and transformations reflect real data already stored for your selected indicators and countries — nothing here is calculated.</div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DataCenterUnitsPanel
